import { useContext } from 'react';
import { BaseWidget } from './BaseWidget';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { SelectControl } from '../controls/SelectControl';
import { ColorControl } from '../controls/ColorControl';
import { FontControl } from '../controls/grouped/FontControl';
import { db } from '../../../../firebase/config';
import { client } from '../../../../hooks/Client';
import { useFirestoreGeneral } from '../../../../firebase/useFirestore';
import { TemplateDataContext } from '../context/TemplateDataContext';
import PublicReportContext from '../../../../StateManagment/PublicReportContext';
import { fetchTemplateData } from '../utils/fetchTemplateData';
import WidgetSourcePlaceholder from '../components/WidgetSourcePlaceholder';
import LoadingSpinner from "../../../common/LoadingSpinner";
import notDeleted from '../../../../helpers/notDeleted';

// Short label for the picker — quotes can be long, so truncate.
const quoteLabel = (q) => {
    const text = (q.Quote || '').trim();
    const short = text.length > 40 ? `${text.slice(0, 40)}…` : text;
    const author = q.Name ? ` — ${q.Name}` : '';
    return (short || 'Quote') + author;
};

/**
 * QuoteWidget — picks one Storytelling quote and renders its text + author.
 * Data resolves per mode, mirroring the other data widgets:
 *   - template preview → templateData.data.quotes (exportTemplateData gatherer)
 *   - live (editor/own tenant) → Firestore `Quotes` by ID
 *
 * @extends {BaseWidget}
 */
export class QuoteWidget extends BaseWidget {
    getName() { return 'Quote'; }
    getIcon() { return <FormatQuoteIcon />; }

    getControls() {
        return {
            quoteId: new SelectControl({
                label: 'Quote',
                description: 'Kies een quote',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    let values = [];

                    if (isTemplate) {
                        const blob = await fetchTemplateData();
                        values = blob?.data?.quotes || [];
                    } else {
                        // Equality + orderBy on a different field needs a composite
                        // index that Quotes doesn't have, so sort client-side.
                        const snap = await db.collection('Quotes')
                            .where('CompagnyID', '==', client || '')
                            .get();
                        values = snap.docs
                            .map(doc => doc.data())
                            .filter(notDeleted)
                            .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
                    }

                    return {
                        choices: values.map(q => ({ value: q.ID, label: quoteLabel(q) })),
                        allowSearch: true,
                    };
                },
            }),
            font: new FontControl({
                label: 'Lettertype',
                description: 'Instellingen voor het lettertype van de quote',
                defaultValue: {
                    fontSize: { value: 24, unit: 'px' },
                    fontStyle: 'italic',
                    alignment: 'left',
                },
            }),
            color: new ColorControl({
                label: 'Kleur',
                description: 'Kleur van de quote',
                defaultValue: '#000000',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} widget={this} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.quoteId - Selected Quote ID
 * @param {object} [props.font] - FontControl value
 * @param {string} [props.color] - Text colour
 */
const Renderer = ({ quoteId, font, color }) => {
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext);

    // Op een gedeelde link (geen login) komt de tekst mee met getPublicLiveReport2:
    // Quotes is alleen voor het eigen team leesbaar. Daar dus niet zelf lezen.
    // AI-CONTEXT: See docs/features/public-livereport-data.md (fase 3)
    const publicReport = useContext(PublicReportContext);
    const isPublic = Boolean(publicReport);
    const liveQuotes = useFirestoreGeneral('Quotes', 'ID', (isTemplate || isPublic) ? '' : (quoteId || ''));
    const quote = isTemplate
        ? (templateData?.data?.quotes || []).find(q => q.ID === quoteId)
        : isPublic
            ? (publicReport.storytelling?.quotes || []).find(q => q.ID === quoteId)
            : (Array.isArray(liveQuotes) && liveQuotes[0]);

    if (!quote) {
        /* Template blob failed to load — show the error, never an endless spinner. */
        if (isTemplate && templateError) {
            return <p className="output-widget-error">Fout bij laden template data.</p>;
        }
        /* Still resolving (hook hasn't answered / template blob loading) →
           render nothing; resolved-but-missing (orphan after a template copy)
           or nothing selected → editor-only select CTA. */
        /* Op een gedeelde link is alles al binnen met de pagina: wat ontbreekt
           hoort niet bij dit rapport, niet 'nog onderweg'. */
        const stillResolving = isTemplate
            ? !templateData
            : !isPublic && (quoteId && !Array.isArray(liveQuotes));
        if (stillResolving) return <LoadingSpinner />;
        return <WidgetSourcePlaceholder ctaLabel="Selecteer quote" />;
    }

    const style = {
        textAlign: font?.alignment,
        fontFamily: font?.fontFamily,
        fontSize: font?.fontSize && `${font.fontSize.value}${font.fontSize.unit}`,
        fontStyle: font?.fontStyle,
        fontWeight: font?.fontWeight,
        textDecoration: font?.textDecoration,
        color: color,
    };

    return (
        <blockquote className="livereport-quote" style={style}>
            <span className="livereport-quote-text">“{quote.Quote}”</span>
            {quote.Name && <span className="livereport-quote-author">{quote.Name}</span>}
        </blockquote>
    );
};
